import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Message, QueryKeys } from '@connect-x-app/shared-domain-models';
import { useQueryClient } from '@tanstack/react-query';

import { useGetPaginatedMessages } from './queries';
import { useAuthentication } from './useAuthentication';

export const useGetMessages = (flightRoomId?: string, channelId?: string, messageId?: string) => {
  const { user } = useAuthentication();
  const queryClient = useQueryClient();

  const prevChannelIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (channelId && prevChannelIdRef.current !== undefined && prevChannelIdRef.current !== channelId) {
      queryClient.removeQueries({
        queryKey: [QueryKeys.MESSAGES, channelId],
      });
    }
    prevChannelIdRef.current = channelId;
  }, [channelId, queryClient]);

  const {
    data,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useGetPaginatedMessages({ channelId, flightRoomId, messageId, user });

  const hasLoadedLastPage = useMemo(() => {
    return !messageId || !hasNextPage;
  }, [messageId, hasNextPage]);

  const messages: Message[] = useMemo(() => {
    return data?.pages?.flatMap((page: { items?: Message[] }) => page.items ?? []) ?? [];
  }, [data]);

  const handlePreviousPage = useCallback(() => {
    if (hasPreviousPage && !isFetchingPreviousPage && !isFetchingNextPage && messages.length) {
      fetchPreviousPage();
    }
  }, [fetchPreviousPage, hasPreviousPage, isFetchingNextPage, isFetchingPreviousPage, messages.length]);

  const handleNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingPreviousPage && !isFetchingNextPage && messages.length) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchingPreviousPage, messages.length]);

  return {
    error,
    fetchNextPage: handleNextPage,
    fetchPreviousPage: handlePreviousPage,
    hasLoadedLastPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    messages,
  };
};
